import React from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';
import CheckIcon from '@mui/icons-material/Check';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { i18n } from 'i18n';
import {
  moveField,
  PivotDropTarget,
  PivotLayout,
  SPOOL_FIELDS,
  SPOOL_MEASURES,
  spoolFieldLabel,
  SpoolField,
  SpoolMeasure
} from './spoolPivot';

const DRAG_TYPE = 'application/x-spool-report-field';

type DropArea = PivotDropTarget['area'];

interface PivotFieldWellsProps {
  layout: PivotLayout;
  onLayoutChange: (layout: PivotLayout) => void;
  measures: SpoolMeasure[];
  onMeasuresChange: (measures: SpoolMeasure[]) => void;
  measureLabel: (measure: SpoolMeasure) => string;
}

const draggedField = (event: React.DragEvent): SpoolField | null => {
  const field = event.dataTransfer.getData(DRAG_TYPE) as SpoolField;
  return SPOOL_FIELDS.includes(field) ? field : null;
};

// Excel-style field wells: drag a field between the list, Rows and Columns, or within Rows to reorder; click a
// listed field to add it as the next row level.
const PivotFieldWells = ({
  layout,
  onLayoutChange,
  measures,
  onMeasuresChange,
  measureLabel,
}: PivotFieldWellsProps) => {
  const theme = useTheme();
  const [dragOver, setDragOver] = React.useState<DropArea | null>(null);
  const [insertAt, setInsertAt] = React.useState<number | null>(null);

  const unusedFields = SPOOL_FIELDS.filter((field) => !layout.rows.includes(field) && layout.column !== field);
  const move = (field: SpoolField, target: PivotDropTarget) => onLayoutChange(moveField(layout, field, target));

  const endDrag = () => {
    setDragOver(null);
    setInsertAt(null);
  };

  const toggleMeasure = (measure: SpoolMeasure) => {
    const isOn = measures.includes(measure);
    if (isOn && measures.length === 1) return;
    onMeasuresChange(SPOOL_MEASURES.filter((m) => (m === measure ? !isOn : measures.includes(m))));
  };

  const dropZone = (area: DropArea, target: () => PivotDropTarget) => ({
    onDragOver: (event: React.DragEvent<HTMLElement>) => {
      if (!event.dataTransfer.types.includes(DRAG_TYPE)) return;
      event.preventDefault();
      setDragOver(area);
      if (area === 'rows' && !(event.target as HTMLElement).closest('[data-row-chip]')) setInsertAt(null);
    },
    onDragLeave: (event: React.DragEvent<HTMLElement>) => {
      if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
      setDragOver((current) => (current === area ? null : current));
    },
    onDrop: (event: React.DragEvent<HTMLElement>) => {
      event.preventDefault();
      const field = draggedField(event);
      const destination = target();
      endDrag();
      if (field) move(field, destination);
    },
  });

  const fieldChip = (field: SpoolField, props: React.ComponentProps<typeof Chip> = {}) => (
    <Chip
      label={spoolFieldLabel(field)}
      size="small"
      draggable
      icon={<DragIndicatorIcon />}
      onDragStart={(e: React.DragEvent) => {
        e.dataTransfer.setData(DRAG_TYPE, field);
        e.dataTransfer.setData('text/plain', field);
        e.dataTransfer.effectAllowed = 'move';
      }}
      onDragEnd={endDrag}
      sx={{ cursor: 'grab' }}
      {...props}
    />
  );

  const insertMarker = (index: number) => (dragOver === 'rows' && (insertAt ?? layout.rows.length) === index ? (
    <Box sx={{
      width: 3, height: 24, borderRadius: 1, bgcolor: 'primary.main',
    }}
    />
  ) : null);

  const placeholder = (text: string) => <Typography variant="body2" color="text.disabled">{text}</Typography>;

  const shelf = (area: DropArea, label: string, flex: string, target: () => PivotDropTarget, content: React.ReactNode) => (
    <Paper
      variant="outlined"
      data-testid={`spool-report-${area}`}
      {...dropZone(area, target)}
      sx={{
        p: 1,
        flex,
        minWidth: 200,
        minHeight: 72,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.75,
        backgroundColor: dragOver === area ? alpha(theme.palette.primary.main, 0.12) : theme.palette.action.hover,
        borderColor: dragOver === area ? theme.palette.primary.main : undefined,
      }}
    >
      <Typography variant="overline" color="text.secondary" lineHeight={1.4}>{label}</Typography>
      <Box sx={{
        display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75,
      }}
      >
        {content}
      </Box>
    </Paper>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {shelf('fields', i18n._('Fields'), '1 1 100%', () => ({ area: 'fields' }), (
        <>
          {unusedFields.map((field) => (
            <React.Fragment key={field}>
              {fieldChip(field, { variant: 'outlined', onClick: () => move(field, { area: 'rows', index: layout.rows.length }) })}
            </React.Fragment>
          ))}
          {unusedFields.length === 0
            ? placeholder(i18n._('Every field is in use'))
            : placeholder(i18n._('Drag into Rows or Columns, or click to add a row'))}
        </>
      ))}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
        {shelf('rows', i18n._('Rows'), '3 1 320px', () => ({ area: 'rows', index: insertAt ?? layout.rows.length }), (
          <>
            {layout.rows.map((field, index) => (
              <React.Fragment key={field}>
                {index > 0 && <ChevronRightIcon fontSize="small" color="disabled" />}
                {insertMarker(index)}
                <Box
                  data-row-chip
                  onDragOver={(event: React.DragEvent<HTMLElement>) => {
                    if (!event.dataTransfer.types.includes(DRAG_TYPE)) return;
                    const rect = event.currentTarget.getBoundingClientRect();
                    setInsertAt(event.clientX > rect.left + rect.width / 2 ? index + 1 : index);
                  }}
                >
                  {fieldChip(field, { color: 'secondary', onDelete: () => move(field, { area: 'fields' }) })}
                </Box>
              </React.Fragment>
            ))}
            {insertMarker(layout.rows.length)}
            {layout.rows.length === 0 && placeholder(i18n._('Drop fields here to group the spools'))}
          </>
        ))}
        {shelf('columns', i18n._('Columns'), '1 1 200px', () => ({ area: 'columns' }), (
          layout.column
            ? fieldChip(layout.column, { color: 'secondary', onDelete: () => move(layout.column as SpoolField, { area: 'fields' }) })
            : placeholder(i18n._('Drop a field to split into columns'))
        ))}
        <Paper
          variant="outlined"
          sx={{
            p: 1,
            flex: '1 1 260px',
            minHeight: 72,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.75,
            backgroundColor: theme.palette.action.hover,
          }}
        >
          <Typography variant="overline" color="text.secondary" lineHeight={1.4}>{i18n._('Values')}</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {SPOOL_MEASURES.map((measure) => {
              const isOn = measures.includes(measure);
              return (
                <Chip
                  key={measure}
                  label={measureLabel(measure)}
                  size="small"
                  color={isOn ? 'secondary' : 'default'}
                  variant={isOn ? 'filled' : 'outlined'}
                  icon={isOn ? <CheckIcon /> : undefined}
                  aria-pressed={isOn}
                  onClick={() => toggleMeasure(measure)}
                />
              );
            })}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default PivotFieldWells;
